import get from 'lodash/get';
import { select, take, put, call } from 'redux-saga/effects';
import * as types from '../../constants/ActionTypes';
import guid from '../../functions/id/guid';
import findEvent from '../../functions/event/findEvent';
import findRoutePoint from '../../functions/route/findRoutePoint';
import {
  addRoutePoint,
  removeRoutePoint,
  editRoutePoint as editRoutePointAction,
} from '../../actions/route';
import { addEvent, deleteEvent, renameEvent } from '../../actions/events';
import { findQuest } from '../../reducers/quests';
import { isSyncQuestEnding } from '../quests';
import { isSendRoutePointEnding } from './routeConstants';
import { syncAddedRoutePoint } from './routePointSyncSaga';

/**
 * Adds event and route point bound to it
 * If event with `eventId` already exist - will not create new one
 * @param {Object} $
 * @param {Number} $.questId - id of the quest point relate
 * @param {String?} $.eventId - id of the event to create
 * @param {RoutePoint} $.routePoint - route point data
 * @param {Boolean} $.isSync - should quest be refetched after all
 */
export function* addRoutePointWithEvent({
  questId,
  eventId,
  routePoint,
  isSync = true,
}) {
  const { routes: route = [], events } = findQuest(
    yield select((state) => state.quests.quests),
    questId
  );

  /** Data for route point actions */
  const routePointExt = {
    ...routePoint,
    order:
      typeof routePoint.order === 'number' && routePoint.order >= 0
        ? routePoint.order
        : route.length,
  };
  const routeActionProps = {
    pointId: get(route[routePointExt.order], 'guid'),
    questId,
    routePoint: routePointExt,
    isSync,
  };

  const boundEventGuid = eventId || guid();
  if (eventId && findEvent(events, boundEventGuid)) {
    yield put(addRoutePoint(routeActionProps));
    return;
  }

  /** Adding new event, which will be bound to geopoint */
  yield put(
    addEvent({
      questId,
      guid: boundEventGuid,
      title: routePointExt.name,
      html: routePointExt.description,
      time: 0,
    })
  );

  /** Adding geopoint locally */
  yield put(
    addRoutePoint({
      ...routeActionProps,
      isSync: false,
      isSend: false,
    })
  );

  /**
   * If event added successfully - send locally stored geopoint
   * If not - removes added geopoint
   */
  const endOfSyncQuest = yield take(isSyncQuestEnding);
  if (endOfSyncQuest.type !== types.SYNC_QUEST_SUCCESS) {
    yield put(
      removeRoutePoint({
        questId,
        pointId: routePointExt.guid,
        isSync: false,
        isSend: false,
      })
    );
  } else {
    yield call(syncAddedRoutePoint, routeActionProps);
  }

  /** If geopoint was not created - will try to delete bound with it event */
  const endOfSendRoute = yield take((action) =>
    isSendRoutePointEnding(action, routePointExt.guid)
  );
  if (endOfSendRoute.type !== types.SEND_ROUTE_POINT_SUCCESS) {
    yield put(deleteEvent({ questId, eventId: boundEventGuid }));
  }
}

/**
 * Renames event and route point
 * @param {Object} action
 * @param {String|*} action.questId - id of the quest containing quest to rename
 * @param {String} action.eventId - id of the event to rename
 * @param {String} action.pointId - guid of the route point to rename
 * @param {String} action.title - event title to set
 */
export function* renameRoutePointWithEvent({
  questId,
  eventId,
  pointId,
  title,
}) {
  const route =
    findQuest(yield select((state) => state.quests.quests), questId).routes ||
    [];
  const routePoint = findRoutePoint(route, pointId);
  if (routePoint) {
    yield put(
      editRoutePointAction({
        ...routePoint,
        name: title,
        questId,
      })
    );
  }

  yield put(
    renameEvent({
      title,
      questId,
      eventId,
    })
  );
}
